import {
  type MarketingRouterTreatment,
  type MarketingRouterTreatmentType,
  getMarketingRouterTreatmentConfig,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

/** Distributes keyof over a union so every member's keys are included. */
type KeysOfUnion<T> = T extends unknown ? keyof T : never;

/** Every configurable field name across all treatment types. */
export type MarketingRouterTreatmentFieldName = Exclude<KeysOfUnion<MarketingRouterTreatment>, 'type'>;

/**
 * An in-progress treatment: a selected type plus whatever field values the
 * operator has entered so far. Values for fields the selected type does not
 * declare are dropped whenever the treatment is normalized (on type switch)
 * or persisted.
 */
export type MarketingRouterTreatmentDraft = { type: MarketingRouterTreatmentType } & Partial<
  Record<MarketingRouterTreatmentFieldName, string>
>;

function getTreatmentFieldValue(treatment: MarketingRouterTreatmentDraft, field: MarketingRouterTreatmentFieldName) {
  return treatment[field] ?? '';
}

/**
 * Builds a treatment payload holding exactly the fields the type's contract
 * config declares, so new treatment types need no code changes here.
 */
function buildTreatmentForType(
  treatment: MarketingRouterTreatmentDraft,
  type: MarketingRouterTreatmentType,
): MarketingRouterTreatmentDraft {
  const config = getMarketingRouterTreatmentConfig(type);
  return {
    type,
    ...Object.fromEntries(
      (config?.fields ?? []).map((field) => [field.field, getTreatmentFieldValue(treatment, field.field)]),
    ),
  };
}

export function normalizeTreatmentForType(
  treatment: MarketingRouterTreatmentDraft,
  type: MarketingRouterTreatmentType,
): MarketingRouterTreatmentDraft {
  return buildTreatmentForType(treatment, type);
}

export function getPersistedTreatmentValue(treatment: MarketingRouterTreatmentDraft): MarketingRouterTreatment {
  // The contract config declares its fields per treatment type, so building
  // from them produces that type's exact payload shape.
  return buildTreatmentForType(treatment, treatment.type) as MarketingRouterTreatment;
}

export function getTreatmentFieldValueForDraft(
  treatment: MarketingRouterTreatmentDraft,
  field: MarketingRouterTreatmentFieldName,
): string {
  return getTreatmentFieldValue(treatment, field);
}
